import { ConfiguracaoDaLoja } from '@/models'

/**
 * Catálogo dos parâmetros da loja que ficavam na aba Outros da tela antiga. A nova tela de loja se monta daqui: título,
 * descrição, onde o parâmetro age, de quem ele depende e como se procura por ele. Parâmetro novo entra aqui, no
 * assunto em que age, e enquanto a tela antiga existir entra também no template dela.
 */

export type IdDoAssunto =
	| 'caixa'
	| 'venda'
	| 'pagamento'
	| 'trocas'
	| 'estoque'
	| 'prevenda'
	| 'pedidos'
	| 'entrada'
	| 'emissao'
	| 'crediario'

export type TipoDeParametro =
	| 'sw'
	| 'num'
	| 'txt'
	| 'email'
	| 'tel'
	| 'senha'
	| 'lista'
	| 'multi'
	| 'tabela'
	| 'loja'
	| 'pdv'
	| 'escolha'

/** O que a loja carregada e o formulário têm em comum (o formulário troca o tipo das tabelas de preço). */
type Configuracao = Partial<Omit<ConfiguracaoDaLoja, 'id' | 'tabelasDePreco'>>

export type CampoDaConfiguracao = keyof Configuracao

export interface AssuntoDaLoja {
	id: IdDoAssunto
	titulo: string
	icone: string
	resumo: string
	/** O caixa aberto só vê a mudança no próximo turno, ao trocar de PDV ou pelo botão de recarregar a loja. */
	avisoCaixa?: boolean
	nota?: string
}

export interface OpcaoDeParametro {
	valor: string
	texto: string
	descricao?: string
	/** Só nas escolhas únicas: o valor que a opção grava em cada interruptor. */
	grava?: Partial<Record<CampoDaConfiguracao, boolean>>
}

export interface ParametroDaLoja {
	/** O nome do campo; nas escolhas únicas, um nome próprio, porque elas gravam mais de um campo. */
	id: string
	campo?: CampoDaConfiguracao
	grava?: CampoDaConfiguracao[]
	assunto: IdDoAssunto
	grupo: string
	tipo: TipoDeParametro
	unidade?: 'R$' | 'dias' | '%' | '% ao dia'
	inteiro?: boolean
	/** As mesmas regras da tela antiga, nos mesmos campos. */
	regra?: 'maiorOuIgualAZero' | 'maiorOuIgualAUm' | 'obrigatorio'
	/** Seletor de tabela de preço filtrado pela loja. */
	porLoja?: boolean
	/** Só para quem tem acesso irrestrito. */
	restrito?: boolean
	/** A mudança só vale para vendas ou títulos novos. */
	soNovas?: boolean
	/** Aparece dentro do pai, quando o valor do pai é mostraSe ('*': sempre; 'preenchido': qualquer valor). */
	pai?: string
	mostraSe?: boolean | string
	desabilitaSe?: { id: string; valor: boolean | string; motivo: string }
	aviso?: { id: string; valor: boolean | string; texto: string }
	titulo: string
	descricao: string
	detalhe?: string
	opcoes?: OpcaoDeParametro[]
	/** Sinônimos do jeito que a loja fala, para a busca. */
	busca: string[]
	onde: string[]
	/** Rótulo na tela antiga: a busca também acha pelo nome de antes. */
	antigo: string
}

export const ASSUNTOS_DA_LOJA: AssuntoDaLoja[] = [
	{ id: 'caixa', titulo: 'Caixa: abertura e fechamento', icone: 'mdi-cash-register', resumo: 'O que o caixa confere ao abrir e fechar o turno, as movimentações e para onde vai o fechamento.', avisoCaixa: true },
	{ id: 'venda', titulo: 'Vendas no caixa', icone: 'mdi-cart-outline', resumo: 'Cliente, preço, desconto, vendedor e os recursos que aparecem durante a venda.', avisoCaixa: true },
	{ id: 'pagamento', titulo: 'Pagamento e TEF', icone: 'mdi-credit-card-outline', resumo: 'Cartão acima do restante, prazo de estorno e a integração com a maquininha.', avisoCaixa: true },
	{ id: 'trocas', titulo: 'Trocas, devoluções e voucher', icone: 'mdi-swap-horizontal', resumo: 'Prazo e regras da troca, o voucher que sobra para o cliente e o vale-troca.', avisoCaixa: true },
	{ id: 'estoque', titulo: 'Estoque', icone: 'mdi-package-variant-closed', resumo: 'Quando o estoque pode ficar negativo, o que volta sozinho e o que fica reservado.' },
	{ id: 'prevenda', titulo: 'Pré-venda e romaneio', icone: 'mdi-clipboard-text-outline', resumo: 'Como a pré-venda nasce, o que ela exige e o romaneio de pedidos.' },
	{ id: 'pedidos', titulo: 'Pedidos, entregas e integração', icone: 'mdi-truck-outline', resumo: 'Conferência e cancelamento de pedidos, frete, etiqueta de entrega e o PDV das integrações.' },
	{ id: 'entrada', titulo: 'Entrada de notas e transferências', icone: 'mdi-file-download-outline', resumo: 'Importação do XML de compra e o recebimento de notas em trânsito entre lojas.' },
	{ id: 'emissao', titulo: 'Emissão de notas (fiscal)', icone: 'mdi-file-document-outline', resumo: 'Validações ao emitir, venda para outro estado e o que vai no XML e na DANFE.' },
	{ id: 'crediario', titulo: 'Crediário e cobrança', icone: 'mdi-account-cash-outline', resumo: 'Juros, multa e carência dos títulos, e a impressão de carnê e confissão de dívida.', nota: 'Juros, multa, carência e juros compostos também valem para os títulos a pagar criados na importação de notas de compra.' },
]

export const PARAMETROS_DA_LOJA: ParametroDaLoja[] = [
	{
		id: 'validaHorarioNaAberturaDeCaixa', campo: 'validaHorarioNaAberturaDeCaixa', assunto: 'caixa', grupo: 'Abertura', tipo: 'sw',
		titulo: 'Conferir o relógio do computador ao abrir o caixa',
		descricao: 'Impede abrir o caixa quando o horário do computador está mais de 5 minutos diferente do horário do servidor.',
		busca: ['hora', 'horario', 'relogio', 'data errada'],
		onde: ['Caixa'],
		antigo: 'Validar horário do computador na abertura do caixa',
	},
	{
		id: 'permiteAbrirLojaComNotaPendente', campo: 'permiteAbrirLojaComNotaPendente', assunto: 'caixa', grupo: 'Abertura', tipo: 'sw',
		titulo: 'Permitir abrir o caixa com notas inválidas',
		descricao: 'Desligado, a abertura lista as vendas dos últimos 30 dias com nota inválida e só deixa abrir depois de corrigir.',
		detalhe: 'Nota inválida: venda não cancelada dos últimos 30 dias cuja nota não está autorizada, cancelada, inutilizada, reservada nem aguardando a SEFAZ voltar (108 e 109), por exemplo rejeitada. Venda com nota não eletrônica não conta. A mesma regra vale no fechamento e no inventário.',
		busca: ['nota pendente', 'rejeitada', 'nao comunicada', 'abertura'],
		onde: ['Caixa'],
		antigo: 'Permite abrir caixa com notas inválidas',
	},
	{
		id: 'permiteFecharCaixaComNotasPendentes', campo: 'permiteFecharCaixaComNotasPendentes', assunto: 'caixa', grupo: 'Fechamento', tipo: 'sw',
		titulo: 'Permitir fechar o caixa com notas inválidas',
		descricao: 'Desligado, o fechamento avisa "Há notas inválidas" e só fecha depois de corrigir as notas dos últimos 30 dias.',
		busca: ['nota pendente', 'rejeitada', 'fechar turno'],
		onde: ['Caixa'],
		antigo: 'Permite fechar caixa com notas inválidas',
	},
	{
		id: 'fazContagemAsCegas', campo: 'fazContagemAsCegas', assunto: 'caixa', grupo: 'Fechamento', tipo: 'sw',
		titulo: 'Fechamento às cegas',
		descricao: 'No fechamento, o operador conta o dinheiro sem ver o valor esperado nem a diferença.',
		busca: ['contagem', 'cego', 'conferencia de caixa', 'quebra de caixa'],
		onde: ['Caixa'],
		antigo: 'Fechamento às cegas',
	},
	{
		id: 'adicionarAnexosEmMovimentacoes', campo: 'adicionarAnexosEmMovimentacoes', assunto: 'caixa', grupo: 'Movimentações', tipo: 'sw',
		titulo: 'Exigir anexo nas movimentações para fechar o caixa',
		descricao: 'O caixa só fecha quando cada movimentação do turno, fora a abertura, tem anexo. Se faltar, abre a lista para anexar.',
		busca: ['sangria', 'reforco', 'comprovante', 'foto', 'anexo'],
		onde: ['Caixa'],
		antigo: 'Anexo nas movimentações ao fechar o caixa',
	},
	{
		id: 'forcarMotivoDeMovimentacao', campo: 'forcarMotivoDeMovimentacao', assunto: 'caixa', grupo: 'Movimentações', tipo: 'sw',
		titulo: 'Exigir motivo nas movimentações de caixa',
		descricao: 'Sangria e reforço só são lançados com o motivo escolhido. Não vale para movimentação de estoque.',
		busca: ['sangria', 'reforco', 'tipo de movimentacao'],
		onde: ['Caixa'],
		antigo: 'Forçar motivo de movimentacao',
	},
	{
		id: 'valorLimiteDeCaixa', campo: 'valorLimiteDeCaixa', assunto: 'caixa', grupo: 'Movimentações', tipo: 'num', unidade: 'R$', regra: 'maiorOuIgualAZero',
		titulo: 'Alerta de sangria no Painel de Caixa',
		descricao: 'No Painel de Caixa, o PDV aberto com dinheiro igual ou acima desse valor fica vermelho. Não bloqueia nada; 0 deixa todos verdes.',
		busca: ['sangria', 'painel de caixa', 'limite', 'dinheiro na gaveta'],
		onde: ['Painel de Caixa'],
		antigo: 'Valor limite de caixa',
	},
	{
		id: 'enviarFechamentoPeloEmail', campo: 'enviarFechamentoPeloEmail', assunto: 'caixa', grupo: 'Envio do fechamento', tipo: 'sw',
		titulo: 'Enviar o fechamento por e-mail',
		descricao: 'Ao fechar o turno, o PDF do fechamento vai para o e-mail informado.',
		busca: ['email', 'relatorio de fechamento', 'pdf'],
		onde: ['Caixa'],
		antigo: 'Enviar Fechamento de Caixa pelo E-mail',
	},
	{
		id: 'emailEnvioFechamento', campo: 'emailEnvioFechamento', assunto: 'caixa', grupo: 'Envio do fechamento', tipo: 'email', pai: 'enviarFechamentoPeloEmail', mostraSe: true,
		titulo: 'E-mail que recebe o fechamento',
		descricao: 'Um endereço só.',
		busca: ['email'],
		onde: ['Caixa'],
		antigo: 'E-mail para envio de Fechamento do Caixa',
	},
	{
		id: 'enviarFechamentoPeloWpp', campo: 'enviarFechamentoPeloWpp', assunto: 'caixa', grupo: 'Envio do fechamento', tipo: 'sw',
		titulo: 'Enviar o fechamento por WhatsApp',
		descricao: 'Ao fechar o turno, o PDF do fechamento vai para o número informado.',
		busca: ['whatsapp', 'wpp', 'zap', 'celular', 'pdf'],
		onde: ['Caixa'],
		antigo: 'Enviar Fechamento de Caixa pelo Whatsapp',
	},
	{
		id: 'wppEnvioFechamento', campo: 'wppEnvioFechamento', assunto: 'caixa', grupo: 'Envio do fechamento', tipo: 'tel', pai: 'enviarFechamentoPeloWpp', mostraSe: true,
		titulo: 'WhatsApp que recebe o fechamento',
		descricao: 'Com DDD, no formato (00) 90000-0000.',
		busca: ['whatsapp', 'telefone', 'celular'],
		onde: ['Caixa'],
		antigo: 'Whatsapp para envio de Fechamento do Caixa',
	},
	{
		id: 'liberaRefreshDeLojaNoCaixa', campo: 'liberaRefreshDeLojaNoCaixa', assunto: 'caixa', grupo: 'Caixa aberto', tipo: 'sw',
		titulo: 'Botão para recarregar a configuração no caixa',
		descricao: 'Põe na barra do caixa um botão que recarrega a loja, os pagamentos e o PDV, depois de confirmar o descarte da venda em andamento.',
		detalhe: 'É o jeito de um caixa já aberto passar a usar a configuração nova sem fechar o turno.',
		busca: ['refresh', 'atualizar', 'recarregar', 'f5'],
		onde: ['Caixa'],
		antigo: 'Liberar botão para recarregar loja no caixa.',
	},
	{
		id: 'lembreteDeCpfCnpj', campo: 'lembreteDeCpfCnpj', assunto: 'venda', grupo: 'Cliente', tipo: 'sw',
		titulo: 'Pedir CPF ou CNPJ a cada venda',
		descricao: 'A cada venda nova o caixa pede o CPF ou CNPJ do cliente, sem obrigar.',
		busca: ['cpf na nota', 'identificar cliente', 'documento'],
		onde: ['Caixa'],
		antigo: 'Lembrete de CPF/CNPJ na venda',
	},
	{
		id: 'bloquearEdicaoCpfCnpjAposPrimeiraBipagem', campo: 'bloquearEdicaoCpfCnpjAposPrimeiraBipagem', assunto: 'venda', grupo: 'Cliente', tipo: 'sw',
		titulo: 'Travar o CPF ou CNPJ depois do primeiro produto',
		descricao: 'Depois de bipar o primeiro produto, o CPF ou CNPJ da venda não pode mais ser trocado.',
		busca: ['cpf', 'bloquear cliente', 'bipagem'],
		onde: ['Caixa'],
		antigo: 'Bloquear campo CPF ou CNPJ',
	},
	{
		id: 'alertarSobreFaltaDeEmailDoCliente', campo: 'alertarSobreFaltaDeEmailDoCliente', assunto: 'venda', grupo: 'Cliente', tipo: 'sw',
		titulo: 'Avisar quando o cliente não tem e-mail',
		descricao: 'Ao seguir para o pagamento, se o cliente não tiver e-mail cadastrado, o caixa pergunta se quer preencher.',
		busca: ['email do cliente', 'cadastro do cliente'],
		onde: ['Caixa'],
		antigo: 'Alertar sobre falta de e-mail do cliente ao seguir para pagamento',
	},
	{
		id: 'exigeAutenticacaoParaLiberarVendaClienteInadimplente', campo: 'exigeAutenticacaoParaLiberarVendaClienteInadimplente', assunto: 'venda', grupo: 'Cliente', tipo: 'sw',
		titulo: 'Exigir autorização para vender a cliente inadimplente',
		descricao: 'A venda para cliente inadimplente só segue com o login de um usuário que tenha permissão para liberar.',
		busca: ['inadimplente', 'devedor', 'atraso', 'liberar venda', 'senha do gerente'],
		onde: ['Caixa'],
		antigo: 'Exigir autenticação para liberar venda a cliente inadimplente',
	},
	{
		id: 'bloquearNovaVenda', campo: 'bloquearNovaVenda', assunto: 'venda', grupo: 'Cliente', tipo: 'sw',
		titulo: 'Recusar venda de cliente com nota inválida no turno',
		descricao: 'Ao finalizar uma venda com cliente identificado, o caixa confere a venda anterior desse CPF ou CNPJ no mesmo turno; se a nota dela estiver inválida, não finaliza.',
		busca: ['nota invalida', 'bloquear venda', 'nota rejeitada'],
		onde: ['Caixa'],
		antigo: 'Bloquear nova venda quando existir nota inválida',
	},
	{
		id: 'podeSelecionarTabelaDePreco', campo: 'podeSelecionarTabelaDePreco', assunto: 'venda', grupo: 'Preço e desconto', tipo: 'sw',
		titulo: 'Escolher a tabela de preço no caixa',
		descricao: 'Põe "Tabela de Preço" no menu do caixa nas vendas do tipo Venda, com as tabelas da aba Tabelas de Preços. PDV com tabela fixa não troca.',
		busca: ['tabela de preco', 'preco de atacado', 'lista de preco'],
		onde: ['Caixa'],
		antigo: 'Permitir selecionar tabela de preço no caixa.',
	},
	{
		id: 'tabelaDePrecoManualPadrao', campo: 'tabelaDePrecoManualPadrao', assunto: 'venda', grupo: 'Preço e desconto', tipo: 'tabela', porLoja: true, pai: 'podeSelecionarTabelaDePreco', mostraSe: true,
		titulo: 'Tabela com que a venda já abre',
		descricao: 'Aplicada ao abrir a venda. Com ela, dá para trocar de tabela mesmo com itens lançados, e os preços são recalculados; sem ela, só antes do primeiro item.',
		busca: ['tabela de preco', 'tabela padrao'],
		onde: ['Caixa'],
		antigo: 'Tabela de Preço Manual Padrão',
	},
	{
		id: 'percentualDeDescontoBase', campo: 'percentualDeDescontoBase', assunto: 'venda', grupo: 'Preço e desconto', tipo: 'num', unidade: '%', regra: 'maiorOuIgualAZero', restrito: true,
		titulo: 'Desconto base da loja',
		descricao: 'Acima de 0, o menu de desconto do caixa ganha o botão "%", que aplica esse percentual somado ao desconto manual. Não vale em trocas nem na Emissão de Notas.',
		busca: ['desconto', 'percentual', 'promocao'],
		onde: ['Caixa'],
		antigo: 'Desconto base',
	},
	{
		id: 'naoPermiteAlterarVendedorEmDemonstracoes', campo: 'naoPermiteAlterarVendedorEmDemonstracoes', assunto: 'venda', grupo: 'Vendedor', tipo: 'sw',
		titulo: 'Manter o vendedor no retorno de demonstração',
		descricao: 'No retorno de uma demonstração, o vendedor não pode ser trocado.',
		busca: ['vendedor', 'demonstracao', 'condicional', 'comissao'],
		onde: ['Caixa'],
		antigo: 'Não permite alterar vendedor em Demonstrações',
	},
	{
		id: 'utilizaOfertaRelacionadaNoCaixa', campo: 'utilizaOfertaRelacionadaNoCaixa', assunto: 'venda', grupo: 'Sugestões e vitrine', tipo: 'sw',
		titulo: '"Quem comprou também levou" no caixa',
		descricao: 'A cada item bipado, mostra até 3 produtos que mais saíram junto dele em vendas anteriores. Usa o histórico de vendas, sem cadastro.',
		busca: ['sugestao', 'oferta', 'venda casada', 'recomendacao'],
		onde: ['Caixa'],
		antigo: 'Utiliza fluxo \'Quem comprou, também levou\' no caixa',
	},
	{
		id: 'sugereProdutoComplementarNoCaixa', campo: 'sugereProdutoComplementarNoCaixa', assunto: 'venda', grupo: 'Sugestões e vitrine', tipo: 'sw',
		titulo: 'Botão de produtos complementares no caixa',
		descricao: 'O botão "Selecionar Complementares" lista os complementares cadastrados nos produtos do carrinho (Produtos relacionados). Abre só quando o operador pede.',
		busca: ['sugestao', 'complementar', 'produto relacionado', 'venda casada'],
		onde: ['Caixa'],
		antigo: 'Sugerir produtos complementares no caixa.',
	},
	{
		id: 'liberaVitrineVirtualNoCaixa', campo: 'liberaVitrineVirtualNoCaixa', assunto: 'venda', grupo: 'Sugestões e vitrine', tipo: 'sw',
		titulo: 'Vitrine virtual no caixa',
		descricao: 'Botão "Vitrine virtual" nas vendas do tipo Venda. Para finalizar, a loja precisa ter a modalidade e o canal "Vitrine Virtual" cadastrados.',
		busca: ['vitrine', 'catalogo', 'mostruario'],
		onde: ['Caixa'],
		antigo: 'Libera vitrine virtual no caixa',
	},
	{
		id: 'exibirAnalyticsNoCaixa', campo: 'exibirAnalyticsNoCaixa', assunto: 'venda', grupo: 'Sugestões e vitrine', tipo: 'sw',
		titulo: 'Painel do cliente no caixa',
		descricao: 'Quando o cliente é identificado por CPF, mostra perfil, métricas, mix de categorias, bags e crediário na área livre do caixa. Não aparece com CNPJ nem no celular.',
		busca: ['analytics', 'perfil do cliente', 'historico do cliente', 'metricas'],
		onde: ['Caixa'],
		antigo: 'Exibir analytics do cliente no caixa',
	},
	{
		id: 'origensDeModalidadeAlteraveis', campo: 'origensDeModalidadeAlteraveis', assunto: 'venda', grupo: 'Depois da venda', tipo: 'multi',
		titulo: 'Origens que permitem trocar a modalidade da venda',
		descricao: 'Nos detalhes da venda, a modalidade só pode ser trocada quando a origem da modalidade atual está marcada. Nenhuma marcada: ninguém troca.',
		opcoes: [
			{ valor: 'Presencial', texto: 'Presencial' },
			{ valor: 'Devolução', texto: 'Devolução' },
			{ valor: 'Demonstração', texto: 'Demonstração' },
			{ valor: 'Ecommerce', texto: 'E-commerce' },
			{ valor: 'Link', texto: 'Link' },
			{ valor: 'Outro', texto: 'Outro' },
		],
		busca: ['modalidade', 'origem', 'detalhes da venda', 'canal'],
		onde: ['Vendas'],
		antigo: 'Origens de Modalidade que Permitem Alteração',
	},
	{
		id: 'trocoParaCartao', campo: 'trocoParaCartao', assunto: 'pagamento', grupo: 'No caixa', tipo: 'sw',
		titulo: 'Aceitar cartão acima do valor que falta',
		descricao: 'Ligado, o operador pode lançar no cartão mais do que o restante da venda e a diferença vira troco. Desligado, as formas Cartão somem quando o valor passa do restante. Não vale para TEF.',
		busca: ['troco', 'cartao', 'troco no cartao', 'valor maior'],
		onde: ['Caixa'],
		antigo: 'Desativa a permissão valores de cartão maiores que o valor total da venda',
	},
	{
		id: 'limiteDeDiasParaCancelamentoAposEmissaoDaNota', campo: 'limiteDeDiasParaCancelamentoAposEmissaoDaNota', assunto: 'pagamento', grupo: 'Estorno', tipo: 'num', unidade: 'dias', inteiro: true, regra: 'maiorOuIgualAZero',
		titulo: 'Prazo para estornar pagamentos de venda com nota',
		descricao: 'Por quantos dias depois da emissão o botão "Cancelar Pagamentos" (estorno, inclusive TEF) aparece em Vendas. 0: venda com nota nunca estorna; sem nota, sempre.',
		busca: ['estorno', 'cancelar pagamento', 'devolver dinheiro', 'tef'],
		onde: ['Vendas', 'Demonstrações'],
		antigo: 'Dias para cancelamento dos pagamentos',
	},
	{
		id: 'integracaoTef', grava: ['integracaoControlPayAtiva', 'integracaoSitefAtiva'], assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'escolha',
		titulo: 'Integração TEF',
		descricao: 'Por onde o caixa cobra cartão e Pix integrados. Com uma integração, a aba Pagamentos libera as formas TEF Crédito, Débito e Pix, e o estorno pode usar o TEF.',
		opcoes: [
			{ valor: 'nenhuma', texto: 'Sem integração TEF', descricao: 'Cartão e Pix lançados à mão, com a maquininha separada.', grava: { integracaoControlPayAtiva: false, integracaoSitefAtiva: false } },
			{ valor: 'controlpay', texto: 'ControlPay (PayGo)', descricao: 'Precisa de configuração no sistema da PayGo; o PDV ganha o campo ID do Terminal ControlPay.', grava: { integracaoControlPayAtiva: true, integracaoSitefAtiva: false } },
			{ valor: 'sitef', texto: 'SiTef (Fiserv)', descricao: 'Precisa do CliSiTef no computador (fale com o suporte) e do ID do terminal aqui e no PDV.', grava: { integracaoControlPayAtiva: false, integracaoSitefAtiva: true } },
		],
		busca: ['tef', 'maquininha', 'pinpad', 'paygo', 'controlpay', 'sitef', 'fiserv', 'clisitef', 'cartao', 'pix'],
		onde: ['Caixa', 'Pagamentos'],
		antigo: 'Permite venda com TEF (ControlPay) e Permite venda com TEF (SiTef)',
	},
	{
		id: 'senhaControlPay', campo: 'senhaControlPay', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'senha', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Senha ControlPay',
		descricao: 'Senha de acesso da loja na ControlPay.',
		busca: ['senha', 'paygo'],
		onde: ['Caixa'],
		antigo: 'Senha ControlPay',
	},
	{
		id: 'senhaTecnicaControlPay', campo: 'senhaTecnicaControlPay', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'senha', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Senha técnica ControlPay',
		descricao: 'Senha técnica fornecida pela PayGo.',
		busca: ['senha', 'paygo'],
		onde: ['Caixa'],
		antigo: 'Senha Técnica ControlPay',
	},
	{
		id: 'urlControlPay', campo: 'urlControlPay', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'txt', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Endereço (URL) da ControlPay',
		descricao: 'Endereço do serviço ControlPay usado pela loja.',
		busca: ['url', 'endereco', 'paygo'],
		onde: ['Caixa'],
		antigo: 'URL ControlPay',
	},
	{
		id: 'adquirenteControlPay', campo: 'adquirenteControlPay', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'txt', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Adquirente do cartão',
		descricao: 'Nome da credenciadora enviado à ControlPay em cada cobrança de cartão (por exemplo, C6 Pay).',
		busca: ['adquirente', 'credenciadora', 'rede', 'cielo', 'stone', 'getnet'],
		onde: ['Caixa'],
		antigo: 'Adquirente',
	},
	{
		id: 'adquirentePixControlPay', campo: 'adquirentePixControlPay', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'lista', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Banco do Pix',
		descricao: 'Banco que recebe o Pix cobrado pela ControlPay.',
		opcoes: [
			{ valor: 'Pix C6 Bank', texto: 'C6 Bank' },
			{ valor: 'Pix Itau', texto: 'Itaú' },
			{ valor: 'PIX SICOOB', texto: 'Sicoob' },
		],
		busca: ['pix', 'banco', 'adquirente pix'],
		onde: ['Caixa'],
		antigo: 'Adquirente Pix',
	},
	{
		id: 'impressaoDeSegundaVia', campo: 'impressaoDeSegundaVia', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'sw', pai: 'integracaoTef', mostraSe: 'controlpay',
		titulo: 'Imprimir a segunda via do comprovante TEF',
		descricao: 'No PDF da NFC-e, cada comprovante de pagamento TEF sai duas vezes.',
		busca: ['segunda via', 'comprovante', 'via do cliente', 'cupom'],
		onde: ['Caixa'],
		antigo: 'Imprimir segunda via de pagamento',
	},
	{
		id: 'idTerminalDaLojaSitef', campo: 'idTerminalDaLojaSitef', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'txt', pai: 'integracaoTef', mostraSe: 'sitef',
		titulo: 'ID do terminal da loja no SiTef',
		descricao: 'Obrigatório para cobrar pelo SiTef. O PDV tem o seu próprio ID.',
		busca: ['terminal', 'fiserv', 'sitef'],
		onde: ['Caixa'],
		antigo: 'ID do terminal da loja no SiTef',
	},
	{
		id: 'senhaSupervisorSitef', campo: 'senhaSupervisorSitef', assunto: 'pagamento', grupo: 'Maquininha (TEF)', tipo: 'senha', pai: 'integracaoTef', mostraSe: 'sitef',
		titulo: 'Senha de supervisor do SiTef',
		descricao: 'Usada no menu administrativo do SiTef no PDV.',
		busca: ['senha', 'supervisor', 'menu administrativo'],
		onde: ['Caixa'],
		antigo: 'Senha Supervisor SiTef',
	},
	{
		id: 'diasParaDevolucao', campo: 'diasParaDevolucao', assunto: 'trocas', grupo: 'Prazo e regras', tipo: 'num', unidade: 'dias', inteiro: true, regra: 'maiorOuIgualAZero', soNovas: true,
		titulo: 'Prazo para devolução',
		descricao: 'Passado o prazo, contado da data da venda, o caixa recusa a devolução (demonstração e consignado ficam de fora). Vazio: sem prazo.',
		detalhe: 'O prazo fica gravado em cada venda (dá para mudar nos detalhes da venda). Também preenche {DIAS_DEVOLUCAO} no texto da Política de troca, na aba Fiscal.',
		busca: ['prazo', 'troca', 'devolver', 'politica de troca', '30 dias', 'dias'],
		onde: ['Caixa'],
		antigo: 'Dias para devolução',
	},
	{
		id: 'devolucaoParcial', campo: 'devolucaoParcial', assunto: 'trocas', grupo: 'Prazo e regras', tipo: 'sw',
		titulo: 'Devolução parcial no caixa',
		descricao: 'Permite devolver só alguns itens de uma venda.',
		busca: ['devolver parte', 'troca parcial'],
		onde: ['Caixa'],
		antigo: 'Permite devolução parcial',
	},
	{
		id: 'permitirTrocaSemIdentificacao', campo: 'permitirTrocaSemIdentificacao', assunto: 'trocas', grupo: 'Prazo e regras', tipo: 'sw',
		titulo: 'Troca sem identificar a venda de origem',
		descricao: 'Mostra na devolução do caixa o botão "Múltiplas trocas / Sem identificação", para trocar sem informar a venda de origem ou juntando mais de uma venda.',
		busca: ['troca sem nota', 'troca sem cupom', 'sem identificacao'],
		onde: ['Caixa'],
		antigo: 'Permitir troca sem identificação',
	},
	{
		id: 'vendedorNaTroca', grava: ['bloqueiaMultiplosVendedoresNaTroca', 'exigeAutorizacaoTrocaDeVendedorNaTroca'], assunto: 'trocas', grupo: 'Prazo e regras', tipo: 'escolha',
		titulo: 'Trocar o vendedor na troca',
		descricao: 'Se a venda da troca pode ficar com outro vendedor, diferente do da devolução.',
		opcoes: [
			{ valor: 'livre', texto: 'Pode trocar', descricao: 'A venda da troca pode ter outro vendedor.', grava: { bloqueiaMultiplosVendedoresNaTroca: false, exigeAutorizacaoTrocaDeVendedorNaTroca: false } },
			{ valor: 'autorizacao', texto: 'Só com autorização', descricao: 'Pede login e senha de quem tem permissão e registra quem autorizou.', grava: { bloqueiaMultiplosVendedoresNaTroca: false, exigeAutorizacaoTrocaDeVendedorNaTroca: true } },
			{ valor: 'bloqueado', texto: 'Mesmo vendedor da devolução', descricao: 'A venda da troca não pode ter vendedor diferente da devolução.', grava: { bloqueiaMultiplosVendedoresNaTroca: true, exigeAutorizacaoTrocaDeVendedorNaTroca: false } },
		],
		busca: ['vendedor', 'comissao', 'troca de vendedor', 'autorizacao'],
		onde: ['Caixa'],
		antigo: 'Bloqueia múltiplos vendedores em troca e Exigir autorização para alterar vendedor em troca',
	},
	{
		id: 'permiteVoucherParcial', campo: 'permiteVoucherParcial', assunto: 'trocas', grupo: 'Voucher', tipo: 'sw',
		titulo: 'Guardar o saldo que sobra do voucher',
		descricao: 'Ligado, quando o cliente paga com voucher e usa menos do que tem, o que sobra vira um voucher novo. Desligado, todos os vouchers abertos do cliente são baixados e a sobra se perde.',
		busca: ['voucher', 'credito', 'vale', 'saldo'],
		onde: ['Caixa'],
		antigo: 'Permite voucher parcial',
	},
	{
		id: 'limiteDeDiasParaExpirarVoucher', campo: 'limiteDeDiasParaExpirarVoucher', assunto: 'trocas', grupo: 'Voucher', tipo: 'num', unidade: 'dias', inteiro: true, regra: 'maiorOuIgualAUm',
		titulo: 'Validade do voucher de troca',
		descricao: 'Dias, a partir da geração, para usar o voucher criado na devolução e na troca. Voucher avulso não segue este prazo. Mínimo 1.',
		busca: ['voucher', 'validade', 'expirar', 'vencer', 'credito'],
		onde: ['Caixa'],
		antigo: 'Dias para expiração do voucher',
	},
	{
		id: 'permiteZerarSaldoVoucher', campo: 'permiteZerarSaldoVoucher', assunto: 'trocas', grupo: 'Voucher', tipo: 'num', unidade: 'R$', inteiro: true, regra: 'maiorOuIgualAZero',
		titulo: 'Sobra pequena da troca vira desconto',
		descricao: 'Se sobrar crédito até esse valor, o caixa pergunta se quer usar todo o saldo; confirmando, vira desconto e não gera voucher. 0 desliga. Só reais inteiros.',
		busca: ['voucher', 'saldo', 'troco da troca', 'diferenca', 'desconto'],
		onde: ['Caixa'],
		antigo: 'Valor máximo para zerar saldo da troca',
	},
	{
		id: 'naoIncluirFreteNoVoucherDaDevolucao', campo: 'naoIncluirFreteNoVoucherDaDevolucao', assunto: 'trocas', grupo: 'Voucher', tipo: 'sw',
		titulo: 'Deixar o frete fora do voucher da devolução',
		descricao: 'O voucher da devolução não inclui o frete pago na venda de origem.',
		busca: ['frete', 'voucher', 'devolucao'],
		onde: ['Caixa'],
		antigo: 'Não incluir frete no voucher da devolução',
	},
	{
		id: 'impressaoDeValeTroca', campo: 'impressaoDeValeTroca', assunto: 'trocas', grupo: 'Vale-troca', tipo: 'sw',
		titulo: 'Vale-troca nos detalhes da venda',
		descricao: 'Libera nos detalhes da venda a opção "Vale Troca", para imprimir quando pedir, com o número de cópias. Não imprime nada sozinho.',
		busca: ['vale troca', 'vale-troca', 'ticket de troca', 'cupom de troca', 'imprimir'],
		onde: ['Vendas'],
		antigo: 'Imprimir vale troca',
	},
	{
		id: 'imprimirValeTrocaNoCupom', campo: 'imprimirValeTrocaNoCupom', assunto: 'trocas', grupo: 'Vale-troca', tipo: 'sw',
		titulo: 'Vale-troca automático no cupom',
		descricao: 'Ao finalizar a venda no caixa, sai 1 vale-troca no mesmo PDF da NFC-e ou do cupom sem valor fiscal (não na DANFE da NF-e).',
		busca: ['vale troca', 'vale-troca', 'ticket de troca', 'cupom', 'nfce', 'imprimir'],
		onde: ['Caixa'],
		antigo: 'Imprimir vale troca no cupom',
	},
	{
		id: 'podeGerarEstoqueNaVenda', campo: 'podeGerarEstoqueNaVenda', assunto: 'estoque', grupo: 'Saldo', tipo: 'sw',
		titulo: 'Vender sem saldo (estoque negativo)',
		descricao: 'Ligado, caixa, Emissão de Notas, pré-venda, carrinho e romaneio vendem sem conferir o saldo, e o estoque pode ficar negativo. Desligado, vender acima do disponível é recusado.',
		detalhe: 'Desligado, a mensagem é "Produto sem estoque", exceto para quem tem a permissão de liberar produto sem estoque. Ligado, o registro de estoque é criado na venda quando não existe.',
		busca: ['estoque negativo', 'sem estoque', 'saldo', 'vender sem estoque'],
		onde: ['Caixa', 'Emissão de Notas', 'Pré-venda', 'Romaneio'],
		antigo: 'Permite estoque negativo',
	},
	{
		id: 'exibeNotificacaoEstoqueNegativo', campo: 'exibeNotificacaoEstoqueNegativo', assunto: 'estoque', grupo: 'Saldo', tipo: 'sw', pai: 'podeGerarEstoqueNaVenda', mostraSe: false,
		titulo: 'Avisar no caixa quando o estoque vai ficar negativo',
		descricao: 'Mostra uma mensagem no caixa quando o item lançado vai deixar o estoque negativo.',
		busca: ['estoque negativo', 'aviso', 'alerta'],
		onde: ['Caixa'],
		antigo: 'Exibe mensagem no caixa quando estoque for ficar negativo',
	},
	{
		id: 'estoqueComplementarDaLoja', campo: 'estoqueComplementarDaLoja', assunto: 'estoque', grupo: 'Saldo', tipo: 'loja', pai: 'podeGerarEstoqueNaVenda', mostraSe: false,
		titulo: 'Loja que completa o estoque dos pedidos da API',
		descricao: 'Em pedidos pela API, soma o estoque desta loja com o da escolhida; se faltar, o sistema emite a transferência e o pedido fatura depois de recebê-la. Não vale no caixa.',
		detalhe: 'Só aparecem lojas do mesmo grupo econômico. Não age na Emissão de Notas.',
		busca: ['estoque complementar', 'cd', 'centro de distribuicao', 'ecommerce', 'api', 'transferencia'],
		onde: ['Pedidos pela API'],
		antigo: 'Estoque complementar da loja',
	},
	{
		id: 'movimentacaoAutomaticaDeEntrada', campo: 'movimentacaoAutomaticaDeEntrada', assunto: 'estoque', grupo: 'Entradas', tipo: 'sw',
		titulo: 'Devolução e troca voltam para o estoque',
		descricao: 'Ligado, devolução e troca no caixa (com nota válida na origem), "Outros" e conserto, e as notas de entrada sem PDV da Emissão de Notas somam no estoque.',
		detalhe: 'Desligado, essas entradas não mexem no saldo. Retorno de demonstração e de consignado, e devolução de venda sem nota, não dependem disto.',
		busca: ['entrada automatica', 'devolucao', 'estoque', 'retorno', 'movimentacao'],
		onde: ['Caixa', 'Emissão de Notas'],
		antigo: 'Movimentação automática de entrada',
	},
	{
		id: 'alteraEstoqueMinimo', campo: 'alteraEstoqueMinimo', assunto: 'estoque', grupo: 'Controle', tipo: 'sw',
		titulo: 'Controlar estoque mínimo',
		descricao: 'A tela Estoques ganha a coluna, o filtro e o campo de estoque mínimo, e um aviso diário vai para quem tem a permissão quando um item chega ao mínimo.',
		busca: ['estoque minimo', 'reposicao', 'ruptura', 'alerta'],
		onde: ['Estoques'],
		antigo: 'Controle Estoque Mínimo',
	},
	{
		id: 'permiteInventarioComNotasInvalidas', campo: 'permiteInventarioComNotasInvalidas', assunto: 'estoque', grupo: 'Controle', tipo: 'sw',
		titulo: 'Permitir inventário com notas inválidas',
		descricao: 'Desligado, o inventário (Estoques, Importar/Exportar) lista as notas inválidas dos PDVs da loja e não conclui até corrigir.',
		busca: ['inventario', 'balanco', 'contagem de estoque', 'nota invalida'],
		onde: ['Estoques'],
		antigo: 'Criar inventário com notas inválidas ou não comunicadas',
	},
	{
		id: 'tiposDePreVendaQueReservamEstoque', campo: 'tiposDePreVendaQueReservamEstoque', assunto: 'estoque', grupo: 'Controle', tipo: 'multi',
		titulo: 'Pré-vendas que reservam estoque',
		descricao: 'As pré-vendas dos tipos marcados reservam o estoque ao serem criadas e a cada item lançado. Não muda enquanto houver pré-venda aberta de um tipo alterado.',
		opcoes: [
			{ valor: 'venda', texto: 'Venda' },
			{ valor: 'demonstracao', texto: 'Demonstração' },
			{ valor: 'consignacao', texto: 'Consignação' },
			{ valor: 'brinde', texto: 'Brinde' },
			{ valor: 'outras saidas', texto: 'Outras saídas' },
			{ valor: 'conserto', texto: 'Conserto' },
			{ valor: 'ativo', texto: 'Ativo' },
			{ valor: 'devolucao saida', texto: 'Devolução de saída' },
			{ valor: 'atacado', texto: 'Atacado' },
			{ valor: 'transferencia', texto: 'Transferência' },
		],
		busca: ['reserva', 'empenho', 'pre venda', 'separar'],
		onde: ['Pré-venda'],
		antigo: 'Tipos de pré-venda que reservam estoque',
	},
	{
		id: 'preVendaPadraoIsAtacado', campo: 'preVendaPadraoIsAtacado', assunto: 'prevenda', grupo: 'Pré-venda nova', tipo: 'sw',
		aviso: { id: 'tabelaDePrecoPreVenda', valor: 'preenchido', texto: 'A tabela de preço da pré-venda só vale para o tipo Venda; com Atacado como padrão, ela não se aplica à pré-venda nova.' },
		titulo: 'Pré-venda nova do tipo Atacado',
		descricao: 'A pré-venda nova já vem como Atacado.',
		busca: ['atacado', 'pre venda', 'tipo padrao'],
		onde: ['Pré-venda'],
		antigo: 'Definir Pré-Venda do tipo Atacado como padrão.',
	},
	{
		id: 'utilizaQtdFracionadaPorPadraoNaPreVenda', campo: 'utilizaQtdFracionadaPorPadraoNaPreVenda', assunto: 'prevenda', grupo: 'Pré-venda nova', tipo: 'sw',
		titulo: 'Quantidade fracionada na pré-venda nova',
		descricao: 'A pré-venda nova já libera quantidades fracionadas, para quem tem a permissão de informar quantidade.',
		busca: ['fracionada', 'metro', 'quilo', 'decimal'],
		onde: ['Pré-venda'],
		antigo: 'Utiliza quantidade fracionada por padrão na pré-venda',
	},
	{
		id: 'tabelaDePrecoPreVenda', campo: 'tabelaDePrecoPreVenda', assunto: 'prevenda', grupo: 'Pré-venda nova', tipo: 'tabela', porLoja: true,
		titulo: 'Tabela de preço da pré-venda',
		descricao: 'A pré-venda nova do tipo Venda já vem com essa tabela, sem deixar trocar.',
		busca: ['tabela de preco', 'pre venda'],
		onde: ['Pré-venda'],
		antigo: 'Tabela de Preço da Pré-Venda',
	},
	{
		id: 'obrigarInformarVendedorNaPreVenda', campo: 'obrigarInformarVendedorNaPreVenda', assunto: 'prevenda', grupo: 'Regras', tipo: 'sw',
		titulo: 'Exigir vendedor na pré-venda',
		descricao: 'A pré-venda só é concluída com o vendedor informado. Não vale para pré-venda criada pela API.',
		busca: ['vendedor', 'obrigatorio', 'comissao'],
		onde: ['Pré-venda'],
		antigo: 'Obrigar informar vendedor na pré-venda',
	},
	{
		id: 'questionarRepresentanteDaPreVenda', campo: 'questionarRepresentanteDaPreVenda', assunto: 'prevenda', grupo: 'Regras', tipo: 'sw',
		titulo: 'Confirmar o representante ao importar pré-venda de atacado',
		descricao: 'Ao importar no caixa ou na Emissão de Notas uma pré-venda do tipo Atacado, pergunta se mantém ou troca o representante.',
		busca: ['representante', 'atacado', 'importar pre venda'],
		onde: ['Caixa', 'Emissão de Notas'],
		antigo: 'Questionar representante ao importar pré venda',
	},
	{
		id: 'trabalharComRomaneio', campo: 'trabalharComRomaneio', assunto: 'prevenda', grupo: 'Romaneio', tipo: 'sw',
		titulo: 'Trabalhar com romaneio de pedidos',
		descricao: 'Mostra a aba Romaneios da loja, e os romaneios ativos ficam disponíveis para importar no caixa. Bom para loja com atendente que lança os produtos.',
		busca: ['romaneio', 'atendente', 'separacao', 'importar no caixa'],
		onde: ['Caixa', 'Loja'],
		antigo: 'Trabalhar com romaneio de pedidos',
	},
	{
		id: 'exigirVerificacaoDeProdutos', campo: 'exigirVerificacaoDeProdutos', assunto: 'pedidos', grupo: 'Pedidos', tipo: 'sw',
		titulo: 'Conferir os produtos antes de faturar o pedido',
		descricao: 'O pedido Pendente ganha a ação "verificar produtos"; até verificar, faturar, DANFE e impressões ficam bloqueados, e o faturamento em massa recusa os pedidos.',
		busca: ['conferencia', 'verificar', 'separacao', 'checagem', 'bipar pedido'],
		onde: ['Pedidos'],
		antigo: 'Exigir verificação de produtos nos pedidos',
	},
	{
		id: 'exigeMotivoDeCancelamento', campo: 'exigeMotivoDeCancelamento', assunto: 'pedidos', grupo: 'Pedidos', tipo: 'sw',
		titulo: 'Exigir motivo ao cancelar pedido',
		descricao: 'O pedido só é cancelado com um motivo informado.',
		busca: ['cancelamento', 'motivo', 'cancelar pedido'],
		onde: ['Pedidos'],
		antigo: 'Exigir motivo ao cancelar um pedido',
	},
	{
		id: 'permiteAlterarSituacaoPedidoComFrete', campo: 'permiteAlterarSituacaoPedidoComFrete', assunto: 'pedidos', grupo: 'Pedidos', tipo: 'sw',
		titulo: 'Alterar à mão a situação de pedido com frete',
		descricao: 'Pedido sem frete sempre pode ter a situação alterada; com frete, só com esta opção ligada.',
		busca: ['situacao', 'status do pedido', 'frete'],
		onde: ['Pedidos'],
		antigo: 'Permite alterar situação de pedidos com frete',
	},
	{
		id: 'permitirValorFreteCIF', campo: 'permitirValorFreteCIF', assunto: 'pedidos', grupo: 'Frete', tipo: 'sw',
		titulo: 'Informar o valor do frete pago pela loja (CIF)',
		descricao: 'Libera o campo "Valor Frete" na edição do frete (pagamento do caixa e resumo da Emissão de Notas) quando o frete é por conta da loja, a modalidade CIF.',
		busca: ['frete', 'cif', 'emitente', 'valor do frete'],
		onde: ['Caixa', 'Emissão de Notas'],
		antigo: 'Permitir informar valor de frete CIF',
	},
	{
		id: 'etiquetaPedido', campo: 'etiquetaPedido', assunto: 'pedidos', grupo: 'Etiqueta de entrega', tipo: 'lista',
		titulo: 'Modelo padrão da etiqueta de entrega',
		descricao: 'Vale quando a transportadora da venda não tem modelo próprio. Só imprime com nota autorizada e código de postagem; o pedido vai para "Aguardando Coleta".',
		opcoes: [
			{ valor: 'Complog', texto: 'Genérico (Complog)' },
			{ valor: 'Correios', texto: 'Correios' },
			{ valor: 'Jadlog', texto: 'Jadlog' },
		],
		busca: ['etiqueta', 'transportadora', 'correios', 'jadlog', 'complog', 'envio'],
		onde: ['Pedidos'],
		antigo: 'Padrão de etiqueta de pedidos',
	},
	{
		id: 'contratoCorreios', campo: 'contratoCorreios', assunto: 'pedidos', grupo: 'Etiqueta de entrega', tipo: 'txt',
		titulo: 'Número do contrato dos Correios',
		descricao: 'Sai como "Contrato: número" na etiqueta modelo Correios.',
		busca: ['correios', 'contrato', 'sedex', 'pac'],
		onde: ['Pedidos'],
		antigo: 'Contrato:',
	},
	{
		id: 'sintaxeBoxlink', campo: 'sintaxeBoxlink', assunto: 'pedidos', grupo: 'Etiqueta de entrega', tipo: 'txt',
		titulo: 'Prefixo do rastreio da Boxlink',
		descricao: 'Quando o código de postagem da nota começa com esse prefixo, a etiqueta vem da Boxlink, no lugar do modelo acima. Preenchido, pede o formato e o token.',
		busca: ['boxlink', 'rastreio', 'prefixo', 'etiqueta'],
		onde: ['Pedidos'],
		antigo: 'Prefixo do código de rastreio Boxlink',
	},
	{
		id: 'tamanhoEtiquetaBoxlink', campo: 'tamanhoEtiquetaBoxlink', assunto: 'pedidos', grupo: 'Etiqueta de entrega', tipo: 'lista', pai: 'sintaxeBoxlink', mostraSe: 'preenchido',
		titulo: 'Formato da etiqueta Boxlink',
		descricao: 'Tamanho pedido à Boxlink, com ou sem o documento fiscal junto.',
		opcoes: [
			{ valor: 'formato_16x10', texto: 'Etiqueta 16 x 10 cm' },
			{ valor: 'formato_16x10_mais_documento_fiscal', texto: 'Etiqueta 16 x 10 cm com o documento fiscal' },
			{ valor: 'formato_10x10', texto: 'Etiqueta 10 x 10 cm' },
			{ valor: 'formato_10x10_mais_documento_fiscal', texto: 'Etiqueta 10 x 10 cm com o documento fiscal' },
			{ valor: 'formato_A4_completa', texto: 'Folha A4 inteira' },
			{ valor: 'formato_A4_4_por_folha', texto: 'A4 com 4 etiquetas por folha' },
			{ valor: 'formato_A4_4_por_folha_mais_documento_fiscal', texto: 'A4 com 4 por folha e o documento fiscal' },
			{ valor: 'formato_A4_6_por_folha', texto: 'A4 com 6 etiquetas por folha' },
			{ valor: 'formato_A4_6_por_folha_mais_documento_fiscal', texto: 'A4 com 6 por folha e o documento fiscal' },
		],
		busca: ['boxlink', 'formato', 'tamanho', 'a4'],
		onde: ['Pedidos'],
		antigo: 'Tamanho do formato PDF (Boxlink)',
	},
	{
		id: 'tokenBoxlink', campo: 'tokenBoxlink', assunto: 'pedidos', grupo: 'Etiqueta de entrega', tipo: 'senha', pai: 'sintaxeBoxlink', mostraSe: 'preenchido',
		titulo: 'Token de acesso da Boxlink',
		descricao: 'Fica salvo e aparece neste campo para quem edita a loja.',
		busca: ['boxlink', 'token', 'chave'],
		onde: ['Pedidos'],
		antigo: 'Token (Boxlink)',
	},
	{
		id: 'pdvIntegracao', campo: 'pdvIntegracao', assunto: 'pedidos', grupo: 'Integração', tipo: 'pdv',
		titulo: 'PDV das vendas por integração',
		descricao: 'Obrigatório para criar venda ou pedido pela API pública, Plug4market ou cupom do vendedor. A venda cai neste PDV quando a integração não informa outro.',
		detalhe: 'Sem ele, a criação é recusada ("A loja não possui uma configuração de PDV para integração"). O turno abre sozinho se não houver um aberto. Transferências não precisam.',
		busca: ['integracao', 'api', 'ecommerce', 'plug4market', 'marketplace', 'pdv'],
		onde: ['Integrações'],
		antigo: 'PDV de Integração',
	},
	{
		id: 'criarProdutoImportacaoEntrada', campo: 'criarProdutoImportacaoEntrada', assunto: 'entrada', grupo: 'Importação do XML de compra', tipo: 'sw',
		titulo: 'Criar o produto que não existe ao importar o XML',
		descricao: 'Item do XML de compra sem cadastro vira produto já preenchido (SKU do fornecedor, nome, EAN, preço e unidade). Desligado, todo item precisa ser vinculado.',
		busca: ['xml', 'nota de compra', 'cadastrar produto', 'fornecedor', 'importacao'],
		onde: ['Importação de Notas'],
		antigo: 'Cria produto ao importar XML de compra',
	},
	{
		id: 'replicarDadosParaTodosProdutosNaImportacao', campo: 'replicarDadosParaTodosProdutosNaImportacao', assunto: 'entrada', grupo: 'Importação do XML de compra', tipo: 'sw',
		titulo: 'Replicar todos os atributos na importação',
		descricao: 'Na ação "replicar atributos", copia marca, estação, tags, gênero e categoria. Desligado, copia só marca e estação.',
		busca: ['replicar', 'atributos', 'marca', 'estacao', 'categoria', 'importacao'],
		onde: ['Importação de Notas'],
		antigo: 'Replicar dados para todos os produtos na importação de notas',
	},
	{
		id: 'criarProdutoNaTabelaPrecoImportacaoEntrada', campo: 'criarProdutoNaTabelaPrecoImportacaoEntrada', assunto: 'entrada', grupo: 'Importação do XML de compra', tipo: 'sw',
		titulo: 'Incluir os produtos importados numa tabela de preço',
		descricao: 'Ao finalizar a importação, inclui ou atualiza cada produto na tabela escolhida, com o preço de compra da nota, a margem e o preço de varejo.',
		busca: ['tabela de preco', 'margem', 'markup', 'importacao'],
		onde: ['Importação de Notas'],
		antigo: 'Criar produto na tabela de preço',
	},
	{
		id: 'tabelaDePrecoIntegracao', campo: 'tabelaDePrecoIntegracao', assunto: 'entrada', grupo: 'Importação do XML de compra', tipo: 'tabela', regra: 'obrigatorio', pai: 'criarProdutoNaTabelaPrecoImportacaoEntrada', mostraSe: true,
		titulo: 'Tabela que recebe os produtos importados',
		descricao: 'Obrigatória com a opção acima ligada; sem ela, a importação não finaliza.',
		busca: ['tabela de preco', 'importacao'],
		onde: ['Importação de Notas'],
		antigo: 'Tabela de Preço de integração',
	},
	{
		id: 'exigirVerificacaoDeProdutosNotaEmTransito', campo: 'exigirVerificacaoDeProdutosNotaEmTransito', assunto: 'entrada', grupo: 'Notas em trânsito', tipo: 'sw',
		titulo: 'Conferir o recebimento de notas em trânsito',
		descricao: '"Receber Nota" vira "Bipar Nota": o estoque entra pela quantidade bipada (até a da nota) e o item não bipado não entra. Desligado, recebe direto pela nota.',
		busca: ['transferencia', 'nota em transito', 'receber', 'bipar', 'conferencia'],
		onde: ['Notas em trânsito'],
		antigo: 'Conferir recebimento de notas em trânsito',
	},
	{
		id: 'permiteConferenciaDeEntradaRfid', campo: 'permiteConferenciaDeEntradaRfid', assunto: 'entrada', grupo: 'Notas em trânsito', tipo: 'sw', pai: 'exigirVerificacaoDeProdutosNotaEmTransito', mostraSe: true,
		titulo: 'Conferir por RFID',
		descricao: 'Ação "Conferir via RFID", que lê as etiquetas RFID dos produtos.',
		busca: ['rfid', 'etiqueta', 'antena', 'leitor'],
		onde: ['Notas em trânsito'],
		antigo: 'Permitir conferência de notas em trânsito por RFID.',
	},
	{
		id: 'exigeConferenciaCompletaNaImportacaoDeNotasEmTransito', campo: 'exigeConferenciaCompletaNaImportacaoDeNotasEmTransito', assunto: 'entrada', grupo: 'Notas em trânsito', tipo: 'sw', pai: 'exigirVerificacaoDeProdutosNotaEmTransito', mostraSe: true,
		titulo: 'Exigir conferência completa',
		descricao: 'Só deixa receber com 100% dos itens bipados. Desligado, a falta pode ser justificada com motivo e a nota entra em parte.',
		busca: ['conferencia completa', 'falta', 'divergencia'],
		onde: ['Notas em trânsito'],
		antigo: 'Obrigar a conferência completa dos produtos na importação de notas em trânsito.',
	},
	{
		id: 'obrigarImpostosIbsCbs', campo: 'obrigarImpostosIbsCbs', assunto: 'emissao', grupo: 'Validações ao emitir', tipo: 'sw',
		titulo: 'Exigir IBS e CBS quando a regra fiscal prevê',
		descricao: 'A nota é recusada quando a regra prevê IBS/CBS e o item ou o total sai sem o grupo. Desligado, a nota sai sem ele. Nota não eletrônica fica de fora.',
		busca: ['ibs', 'cbs', 'reforma tributaria', 'imposto'],
		onde: ['Emissão de Notas', 'Caixa'],
		antigo: 'Obrigar uso dos impostos IBS/CBS ao faturar',
	},
	{
		id: 'permiteVendaEntreCnpjsRaiz', campo: 'permiteVendaEntreCnpjsRaiz', assunto: 'emissao', grupo: 'Validações ao emitir', tipo: 'sw',
		titulo: 'Permitir venda entre CNPJs da mesma raiz',
		descricao: 'Desligado, a venda para cliente CNPJ com os mesmos 8 primeiros dígitos da loja é recusada ao gravar, no caixa, na emissão e nas integrações.',
		busca: ['cnpj raiz', 'filial', 'matriz', 'mesma empresa'],
		onde: ['Caixa', 'Emissão de Notas', 'Integrações'],
		antigo: 'Permite emissão de venda entre CNPJs raiz',
	},
	{
		id: 'utilizarPrecoDeCompra', campo: 'utilizarPrecoDeCompra', assunto: 'emissao', grupo: 'Validações ao emitir', tipo: 'sw',
		titulo: 'Usar o preço de compra quando o custo estiver zerado',
		descricao: 'Nas notas a preço de custo (brinde, transferência, uniforme, devolução de saída), custo zero usa o preço de compra da tabela. Desligado, a nota é recusada.',
		detalhe: 'Ordem da tabela: promocional vigente, a do tipo de cliente e a padrão. Desligado, a mensagem é "Preço de custo não encontrado".',
		busca: ['preco de custo', 'custo zerado', 'transferencia', 'brinde', 'uniforme'],
		onde: ['Emissão de Notas', 'Caixa'],
		antigo: 'Faturar com preço de custo informado na ausência do custo fiscal.',
	},
	{
		id: 'naoQuestionarIdDestParaContribuinte', campo: 'naoQuestionarIdDestParaContribuinte', assunto: 'emissao', grupo: 'Venda para outro estado', tipo: 'sw',
		titulo: 'Destino da nota pelo endereço do cliente contribuinte',
		descricao: 'Para cliente que não é ISENTO (ou venda sem cliente), o caixa não pergunta se a nota é presencial: compara a UF do endereço com a da loja.',
		detalhe: 'Vale no caixa, nos detalhes da venda, no carrinho e na reimpressão; a Emissão de Notas continua perguntando. A resposta define o destino da operação (idDest) e a presença (indPres) no XML.',
		busca: ['iddest', 'destino da operacao', 'fora do estado', 'interestadual', 'presencial', 'contribuinte', 'uf'],
		onde: ['Caixa', 'Vendas'],
		antigo: 'Considerar endereço do cliente Contribuinte para idDest',
	},
	{
		id: 'usarIdDestPresencialParaClienteIsento', campo: 'usarIdDestPresencialParaClienteIsento', assunto: 'emissao', grupo: 'Venda para outro estado', tipo: 'sw',
		aviso: { id: 'exigirAutorizacaoParaVendaForaDoEstado', valor: true, texto: 'Com esta opção ligada, a autorização abaixo deixa de ser pedida no caixa e nos detalhes da venda.' },
		titulo: 'Venda para cliente ISENTO sempre presencial',
		descricao: 'Para cliente com IE ISENTO, não pergunta e marca a operação como interna, mesmo com endereço de outra UF.',
		busca: ['iddest', 'isento', 'presencial', 'fora do estado', 'uf'],
		onde: ['Caixa', 'Vendas'],
		antigo: 'Usar idDest presencial para cliente Isentos',
	},
	{
		id: 'exigirAutorizacaoParaVendaForaDoEstado', campo: 'exigirAutorizacaoParaVendaForaDoEstado', assunto: 'emissao', grupo: 'Venda para outro estado', tipo: 'sw',
		titulo: 'Pedir autorização para vender a ISENTO de outro estado',
		descricao: 'Pede um usuário com permissão quando a venda é não presencial (fora do estado) e o cliente tem IE ISENTO. Em Pedidos, basta o cliente ser ISENTO.',
		detalhe: 'O faturamento em massa de pedidos recusa esses pedidos.',
		busca: ['autorizacao', 'fora do estado', 'nao contribuinte', 'isento', 'interestadual'],
		onde: ['Caixa', 'Emissão de Notas', 'Pedidos'],
		antigo: 'Exigir autorização para faturar para cliente não contribuinte',
	},
	{
		id: 'permitirCriarNotaDeDemonstracaoParaForaDoEstado', campo: 'permitirCriarNotaDeDemonstracaoParaForaDoEstado', assunto: 'emissao', grupo: 'Venda para outro estado', tipo: 'sw',
		titulo: 'Permitir demonstração para cliente de outro estado',
		descricao: 'Desligado, bloqueia a demonstração para cliente de outra UF (ou sem UF) ao finalizar no caixa, ao imprimir a NF-e nos detalhes e ao faturar o carrinho.',
		busca: ['demonstracao', 'condicional', 'fora do estado', 'uf'],
		onde: ['Caixa', 'Vendas'],
		antigo: 'Permite nota de demonstração para fora do estado',
	},
	{
		id: 'tipoDeCodigo', campo: 'tipoDeCodigo', assunto: 'emissao', grupo: 'O que vai na nota', tipo: 'lista',
		titulo: 'Código do item na nota e no cupom',
		descricao: 'Código do produto (cProd) no XML da NF-e e da NFC-e e na coluna de código do cupom sem valor fiscal. Com EAN, usa o primeiro EAN do item, quando houver.',
		opcoes: [
			{ valor: 'Sku', texto: 'SKU' },
			{ valor: 'Ean', texto: 'EAN (código de barras)' },
		],
		busca: ['sku', 'ean', 'codigo de barras', 'cprod', 'cupom', 'danfe'],
		onde: ['Emissão de Notas', 'Caixa'],
		antigo: 'Código para impressão de documentos',
	},
	{
		id: 'imprimirNaNotaSkuPai', campo: 'imprimirNaNotaSkuPai', assunto: 'emissao', grupo: 'O que vai na nota', tipo: 'sw', pai: 'tipoDeCodigo', mostraSe: '*',
		desabilitaSe: { id: 'tipoDeCodigo', valor: 'Ean', motivo: 'Não funciona com o código do item em EAN.' },
		titulo: 'SKU do produto pai na DANFE',
		descricao: 'Troca o código do item pelo SKU do produto pai só no PDF da DANFE (NF-e e NFC-e), sem mexer no XML.',
		busca: ['sku pai', 'produto pai', 'referencia', 'danfe'],
		onde: ['Emissão de Notas'],
		antigo: 'Imprimir na nota o SKU do Produto Pai',
	},
	{
		id: 'descricaoDoProdutoReferenciaNaNota', campo: 'descricaoDoProdutoReferenciaNaNota', assunto: 'emissao', grupo: 'O que vai na nota', tipo: 'sw',
		titulo: 'Descrição do produto pai na nota',
		descricao: 'O item variante sai com a descrição do produto pai, no XML e na DANFE. Desligado, sai o nome completo da variante.',
		busca: ['descricao', 'produto pai', 'referencia', 'nome do produto', 'xprod'],
		onde: ['Emissão de Notas'],
		antigo: 'Exibir somente descrição do produto referência na DANFE.',
	},
	{
		id: 'informarInfoAdicionalDanfe', campo: 'informarInfoAdicionalDanfe', assunto: 'emissao', grupo: 'O que vai na nota', tipo: 'sw',
		titulo: 'Informações automáticas na nota',
		descricao: 'Inclui no XML os tributos aproximados (IBPT) e "Ponto de Venda/Identificador/Pagamento". Desligado, ficam o texto da aba Fiscal, os dados da venda, sorteio e Procon.',
		busca: ['informacoes adicionais', 'ibpt', 'tributos', 'lei da transparencia', 'infcpl', 'danfe'],
		onde: ['Emissão de Notas', 'Caixa'],
		antigo: 'Emitir DANFE com informações automáticas',
	},
	{
		id: 'trabalhaComNotaFiscalDeVarejo', campo: 'trabalhaComNotaFiscalDeVarejo', assunto: 'emissao', grupo: 'O que vai na nota', tipo: 'sw',
		titulo: 'Partilha do ICMS na nota de varejo',
		descricao: 'Nota para cliente não contribuinte (sem cliente, sem IE ou ISENTO) recebe nas informações adicionais o texto do Decreto 93/2015 com a partilha do ICMS.',
		busca: ['varejo', 'partilha', 'icms', 'difal', 'decreto', 'fcp'],
		onde: ['Emissão de Notas', 'Caixa'],
		antigo: 'Trabalha com nota fiscal de varejo',
	},
	{
		id: 'alterarDataHoraDaVenda', campo: 'alterarDataHoraDaVenda', assunto: 'emissao', grupo: 'Datas e transações', tipo: 'sw',
		titulo: 'Data da venda passa a ser a da emissão',
		descricao: 'Quando a nota de uma venda sem pedido vai para a SEFAZ, a data e a hora da venda viram as da emissão. Muda os relatórios por data.',
		busca: ['data da venda', 'hora', 'emissao', 'relatorio'],
		onde: ['Emissão de Notas'],
		antigo: 'Alterar data/hora da venda para data/hora da emissão da nota',
	},
	{
		id: 'alterarDataHoraDoPedido', campo: 'alterarDataHoraDoPedido', assunto: 'emissao', grupo: 'Datas e transações', tipo: 'sw',
		titulo: 'Data do pedido passa a ser a da emissão',
		descricao: 'O mesmo, para as vendas ligadas a pedido.',
		busca: ['data do pedido', 'hora', 'emissao'],
		onde: ['Pedidos'],
		antigo: 'Alterar data/hora do pedido para data/hora da emissão da nota',
	},
	{
		id: 'utilizaTransacaoDeConversao', campo: 'utilizaTransacaoDeConversao', assunto: 'emissao', grupo: 'Datas e transações', tipo: 'sw',
		titulo: 'Regra fiscal de conversão para demonstração',
		descricao: 'A venda gerada ao converter uma demonstração usa a regra fiscal da transação Conversão, e não a de venda.',
		busca: ['conversao', 'demonstracao', 'condicional', 'regra fiscal', 'cfop'],
		onde: ['Caixa', 'Vendas'],
		antigo: 'Utiliza transação de conversão',
	},
	{
		id: 'podeDigitarNotaManual', campo: 'podeDigitarNotaManual', assunto: 'emissao', grupo: 'Tela de Emissão de Notas', tipo: 'sw',
		titulo: 'Digitação manual da tributação',
		descricao: 'Mostra a opção "Digitação manual", para digitar os dados fiscais em vez de usar a regra fiscal. Com itens na nota, o modo trava.',
		busca: ['digitacao manual', 'nota manual', 'tributacao', 'cst'],
		onde: ['Emissão de Notas'],
		antigo: 'Permite digitação manual de nota fiscal',
	},
	{
		id: 'devolucaoParcialNaEmissaoDeNotas', campo: 'devolucaoParcialNaEmissaoDeNotas', assunto: 'emissao', grupo: 'Tela de Emissão de Notas', tipo: 'sw',
		titulo: 'Excluir itens devolvidos na Emissão de Notas',
		descricao: 'Com itens devolvidos na nota, aparece o botão de excluir item. Independe da devolução parcial do caixa.',
		busca: ['devolucao parcial', 'excluir item', 'emissao'],
		onde: ['Emissão de Notas'],
		antigo: 'Permite devolução parcial na emissão de notas',
	},
	{
		id: 'trabalhaComJurosComposto', campo: 'trabalhaComJurosComposto', assunto: 'crediario', grupo: 'Juros e multa', tipo: 'sw', soNovas: true,
		titulo: 'Juros compostos',
		descricao: 'Ligado, os juros são compostos; desligado, simples.',
		busca: ['juros', 'composto', 'simples', 'crediario'],
		onde: ['Contas a receber'],
		antigo: 'Trabalhar com juros composto',
	},
	{
		id: 'percentDeJurosDoCrediario', campo: 'percentDeJurosDoCrediario', assunto: 'crediario', grupo: 'Juros e multa', tipo: 'num', unidade: '% ao dia', regra: 'maiorOuIgualAZero', soNovas: true,
		titulo: 'Juros por dia de atraso',
		descricao: 'Aplicado dia a dia desde o vencimento.',
		busca: ['juros', 'mora', 'atraso', 'taxa'],
		onde: ['Contas a receber'],
		antigo: 'Percentual de Juros Diário',
	},
	{
		id: 'multaDoCrediario', campo: 'multaDoCrediario', assunto: 'crediario', grupo: 'Juros e multa', tipo: 'num', unidade: '%', regra: 'maiorOuIgualAZero', soNovas: true,
		titulo: 'Multa por atraso',
		descricao: 'Aplicada uma vez sobre o valor em aberto no fim da carência, em Crediário e Boleto.',
		busca: ['multa', 'atraso', 'boleto'],
		onde: ['Contas a receber'],
		antigo: 'Multa',
	},
	{
		id: 'carenciaDoCrediario', campo: 'carenciaDoCrediario', assunto: 'crediario', grupo: 'Juros e multa', tipo: 'num', unidade: 'dias', inteiro: true, regra: 'maiorOuIgualAZero', soNovas: true,
		titulo: 'Carência',
		descricao: 'Dias depois do vencimento sem encargo. Passada a carência, a multa entra e os juros contam desde o vencimento.',
		busca: ['carencia', 'tolerancia', 'vencimento'],
		onde: ['Contas a receber'],
		antigo: 'Carência',
	},
	{
		id: 'calcularJurosComMulta', campo: 'calcularJurosComMulta', assunto: 'crediario', grupo: 'Juros e multa', tipo: 'sw',
		titulo: 'Juros também sobre a multa',
		descricao: 'A base dos juros inclui a multa em aberto. Vale na hora do cálculo, então alcança também os títulos antigos.',
		busca: ['juros', 'multa', 'base de calculo'],
		onde: ['Contas a receber'],
		antigo: 'Calcular os juros juntamente com a multa',
	},
	{
		id: 'unificarCarneEhConfissaoDeDivida', campo: 'unificarCarneEhConfissaoDeDivida', assunto: 'crediario', grupo: 'Impressão', tipo: 'sw',
		titulo: 'Carnê e confissão de dívida numa impressão só',
		descricao: 'Em vendas com Crediário ou Boleto, os dois saem combinados numa impressão. Ligado, o modelo abaixo não vale.',
		busca: ['carne', 'confissao de divida', 'imprimir', 'crediario'],
		onde: ['Vendas'],
		antigo: 'Unificar Confissão de Divida e Carnê',
	},
	{
		id: 'layoutConfissaoDeDivida', campo: 'layoutConfissaoDeDivida', assunto: 'crediario', grupo: 'Impressão', tipo: 'lista',
		desabilitaSe: { id: 'unificarCarneEhConfissaoDeDivida', valor: true, motivo: 'Com carnê e confissão numa impressão só, o modelo não é usado.' },
		titulo: 'Modelo da confissão de dívida',
		descricao: 'Layout da confissão de dívida impressa nas vendas com Crediário ou Boleto.',
		opcoes: [
			{ valor: 'Modelo 1', texto: 'Modelo 1: padrão, com os itens' },
			{ valor: 'Modelo 2', texto: 'Modelo 2: Bazar' },
		],
		busca: ['confissao de divida', 'modelo', 'layout', 'bazar'],
		onde: ['Vendas'],
		antigo: 'Layout confissão de dívida:',
	},
]

export const PARAMETRO_POR_ID: Record<string, ParametroDaLoja> = PARAMETROS_DA_LOJA.reduce(
	(mapa, parametro) => ({ ...mapa, [parametro.id]: parametro }),
	{} as Record<string, ParametroDaLoja>,
)

export const ASSUNTO_POR_ID = ASSUNTOS_DA_LOJA.reduce(
	(mapa, assunto) => ({ ...mapa, [assunto.id]: assunto }),
	{} as Record<IdDoAssunto, AssuntoDaLoja>,
)

export function estaVazio(valor: unknown): boolean {
	return valor === null || valor === undefined || valor === '' || (Array.isArray(valor) && !valor.length)
}

/** Nas escolhas únicas, a opção cujos interruptores batem com os da loja; nenhuma, se a loja tem uma combinação inválida. */
export function lerParametro(configuracao: Configuracao, parametro: ParametroDaLoja): unknown {
	if (parametro.tipo === 'escolha') {
		const opcao = (parametro.opcoes || []).find(({ grava }) =>
			Object.entries(grava || {}).every(([campo, valor]) => !!configuracao[campo as CampoDaConfiguracao] === valor),
		)
		return opcao ? opcao.valor : null
	}
	return parametro.campo ? configuracao[parametro.campo] : undefined
}

export interface CampoGravado {
	campo: CampoDaConfiguracao
	valor: unknown
}

/** Campos e valores que gravar o parâmetro muda. A tela aplica com $set, para o campo que não veio da API ser reativo. */
export function camposAoGravar(parametro: ParametroDaLoja, valor: unknown): CampoGravado[] {
	if (parametro.tipo === 'escolha') {
		const opcao = (parametro.opcoes || []).find(opcao => opcao.valor === valor)
		const grava = (opcao && opcao.grava) || {}
		return Object.keys(grava).map(campo => ({
			campo: campo as CampoDaConfiguracao,
			valor: grava[campo as CampoDaConfiguracao],
		}))
	}
	return parametro.campo ? [{ campo: parametro.campo, valor }] : []
}

export function paiSatisfeito(configuracao: Configuracao, parametro: ParametroDaLoja): boolean {
	if (!parametro.pai) return true
	const pai = PARAMETRO_POR_ID[parametro.pai]
	if (pai && !paiSatisfeito(configuracao, pai)) return false
	if (parametro.mostraSe === '*') return true
	const valorDoPai = pai ? lerParametro(configuracao, pai) : undefined
	if (parametro.mostraSe === 'preenchido') return !estaVazio(valorDoPai)
	if (typeof parametro.mostraSe === 'boolean') return !!valorDoPai === parametro.mostraSe
	return valorDoPai === parametro.mostraSe
}

function condicaoAtendida(configuracao: Configuracao, condicao: { id: string; valor: boolean | string }): boolean {
	const parametro = PARAMETRO_POR_ID[condicao.id]
	const valor = parametro ? lerParametro(configuracao, parametro) : undefined
	if (condicao.valor === 'preenchido') return !estaVazio(valor)
	if (typeof condicao.valor === 'boolean') return !!valor === condicao.valor
	return valor === condicao.valor
}

/** O motivo, quando o parâmetro fica visível mas não pode ser mexido. */
export function motivoDeDesabilitado(configuracao: Configuracao, parametro: ParametroDaLoja): string | null {
	return parametro.desabilitaSe && condicaoAtendida(configuracao, parametro.desabilitaSe)
		? parametro.desabilitaSe.motivo
		: null
}

export function parametroLigado(configuracao: Configuracao, parametro: ParametroDaLoja): boolean {
	const valor = lerParametro(configuracao, parametro)
	if (parametro.tipo === 'sw') return valor === true
	if (parametro.tipo === 'escolha') return !!parametro.opcoes && valor !== parametro.opcoes[0].valor
	if (parametro.tipo === 'num') return !estaVazio(valor) && Number(valor) !== 0
	return !estaVazio(valor)
}

/** Aviso de combinação que conflita, mostrado no próprio parâmetro enquanto ele está ligado. */
export function avisoAtivo(configuracao: Configuracao, parametro: ParametroDaLoja): string | null {
	if (!parametro.aviso || !parametroLigado(configuracao, parametro)) return null
	return condicaoAtendida(configuracao, parametro.aviso) ? parametro.aviso.texto : null
}

export function filhosDe(id: string, lista: ParametroDaLoja[] = PARAMETROS_DA_LOJA): ParametroDaLoja[] {
	return lista.filter(parametro => parametro.pai === id)
}

/** Os parâmetros da lista que não ficam dentro de outro da mesma lista. */
export function raizesDe(lista: ParametroDaLoja[]): ParametroDaLoja[] {
	const ids = new Set(lista.map(({ id }) => id))
	return lista.filter(parametro => !parametro.pai || !ids.has(parametro.pai))
}

/** Texto curto da condição do pai, para quem acha o parâmetro pela busca enquanto ele está escondido. */
export function condicaoDoPai(parametro: ParametroDaLoja): string {
	const pai = parametro.pai ? PARAMETRO_POR_ID[parametro.pai] : null
	if (!pai || parametro.mostraSe === '*') return ''
	if (pai.tipo === 'escolha') {
		const opcao = (pai.opcoes || []).find(({ valor }) => valor === parametro.mostraSe)
		return `Aparece com "${opcao ? opcao.texto : parametro.mostraSe}" em ${pai.titulo}.`
	}
	if (parametro.mostraSe === 'preenchido') return `Aparece com "${pai.titulo}" preenchido.`
	return `Aparece com "${pai.titulo}" ${parametro.mostraSe ? 'ligado' : 'desligado'}.`
}

function idOuValor(valor: unknown): unknown {
	return valor && typeof valor === 'object' && !Array.isArray(valor) && 'id' in (valor as object)
		? (valor as { id: unknown }).id
		: valor
}

/** Vazio com vazio, número com texto do mesmo número e tabela de preço pelo id contam como iguais. */
export function valoresIguais(a: unknown, b: unknown): boolean {
	if (estaVazio(a) && estaVazio(b)) return true
	const x = idOuValor(a)
	const y = idOuValor(b)
	if (JSON.stringify(x) === JSON.stringify(y)) return true
	const numerico = (valor: unknown) =>
		(typeof valor === 'number' || (typeof valor === 'string' && valor.trim() !== '')) && !isNaN(Number(valor))
	return numerico(x) && numerico(y) && Number(x) === Number(y)
}

/** Fora do valor de loja nova. Senha não entra, e campo vazio só conta nos interruptores. */
export function foraDoPadrao(
	configuracao: Configuracao,
	padrao: Configuracao,
	parametro: ParametroDaLoja,
): boolean {
	if (parametro.tipo === 'senha') return false
	const valor = lerParametro(configuracao, parametro)
	if (estaVazio(valor) && parametro.tipo !== 'sw') return false
	return !valoresIguais(valor, lerParametro(padrao, parametro))
}
